/**
 * MUTAGIM.SHOP — Admin Reviews Queue Page
 * File d'attente de modération des avis clients
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft, Star, Check, X, MessageSquare, Clock, CheckCircle, XCircle,
  Loader2, RefreshCw, ExternalLink, Edit2, Sparkles, Wand2
} from 'lucide-react';
import { AdminLayout } from '../components/admin/AdminLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function StarRating({ rating, interactive = false, onRatingChange }) {
  const [hoverRating, setHoverRating] = useState(0);
  
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`h-4 w-4 ${
            (interactive ? (hoverRating || rating) : rating) >= star 
              ? 'fill-yellow-400 text-yellow-400' 
              : 'text-gray-600'
          } ${interactive ? 'cursor-pointer' : ''}`}
          onMouseEnter={() => interactive && setHoverRating(star)}
          onMouseLeave={() => interactive && setHoverRating(0)}
          onClick={() => interactive && onRatingChange?.(star)}
        />
      ))}
    </div>
  );
}

export function AdminReviewsQueuePage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.split('-')[0] || 'fr';
  
  const [isLoading, setIsLoading] = useState(true);
  const [token, setToken] = useState('');
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [selectedReviews, setSelectedReviews] = useState([]);
  const [activeTab, setActiveTab] = useState('pending');
  const [isProcessing, setIsProcessing] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0 });
  
  // État pour le modal d'édition
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [editForm, setEditForm] = useState({
    author_name: '',
    rating: 5,
    title_fr: '',
    title_en: '',
    title_he: '',
    content_fr: '',
    content_en: '',
    content_he: ''
  });
  const [isSaving, setIsSaving] = useState(false);
  
  // État pour le modal de réécriture IA
  const [showAIModal, setShowAIModal] = useState(false);
  const [aiPrompt, setAIPrompt] = useState('');
  const [aiProcessing, setAIProcessing] = useState(false);
  const [aiProgress, setAIProgress] = useState({ current: 0, total: 0 });
  const [aiResults, setAIResults] = useState([]);

  useEffect(() => {
    const storedToken = localStorage.getItem('admin_token');
    if (storedToken) {
      setToken(storedToken);
      fetchReviews(storedToken, 'pending');
    } else {
      navigate('/');
    }
  }, [navigate]);

  const fetchReviews = async (authToken, status = activeTab, page = 1) => {
    setIsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/products/admin/reviews/queue?status=${status}&page=${page}&limit=${pagination.limit}`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setReviews(data.reviews || []);
        setStats(data.stats || { pending: 0, approved: 0, rejected: 0 });
        setPagination(prev => ({
          ...prev,
          page: page,
          total: data.total || data.reviews?.length || 0
        }));
      } else if (res.status === 401) {
        navigate('/');
      }
    } catch (err) {
      console.error('Error fetching reviews:', err);
      toast.error('Erreur lors du chargement des avis');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTabChange = (status) => {
    setActiveTab(status);
    setSelectedReviews([]);
    fetchReviews(token, status);
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedReviews(reviews.map(r => r.id));
    } else {
      setSelectedReviews([]);
    }
  };

  const handleSelectReview = (reviewId, checked) => {
    if (checked) {
      setSelectedReviews(prev => [...prev, reviewId]);
    } else {
      setSelectedReviews(prev => prev.filter(id => id !== reviewId));
    }
  };

  const handleModerateOne = async (reviewId, action) => {
    try {
      const res = await fetch(`${API_URL}/api/products/admin/reviews/${reviewId}/moderate?action=${action}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success(action === 'approve' ? 'Avis approuvé' : 'Avis rejeté');
        fetchReviews(token, activeTab);
      } else {
        toast.error('Erreur lors de la modération');
      }
    } catch (err) {
      toast.error('Erreur de connexion');
    }
  };

  const handleBulkModerate = async (action) => {
    if (selectedReviews.length === 0) {
      toast.error('Sélectionnez au moins un avis');
      return;
    }
    
    setIsProcessing(true);
    try {
      const res = await fetch(`${API_URL}/api/products/admin/reviews/bulk-moderate?action=${action}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(selectedReviews)
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`${data.moderated_count} avis ${action === 'approve' ? 'approuvés' : 'rejetés'}`);
        setSelectedReviews([]);
        fetchReviews(token, activeTab);
      } else {
        toast.error('Erreur lors de la modération en masse');
      }
    } catch (err) {
      toast.error('Erreur de connexion');
    } finally {
      setIsProcessing(false);
    }
  };

  // Ouvrir le modal d'édition
  const handleEditReview = (review) => {
    setEditingReview(review);
    const title = review.title;
    setEditForm({
      author_name: review.author_name || '',
      rating: review.rating || 5,
      title_fr: typeof title === 'object' ? (title?.fr || '') : (title || ''),
      title_en: typeof title === 'object' ? (title?.en || '') : '',
      title_he: typeof title === 'object' ? (title?.he || '') : '',
      content_fr: review.content?.fr || (typeof review.content === 'string' ? review.content : ''),
      content_en: review.content?.en || '',
      content_he: review.content?.he || ''
    });
    setShowEditModal(true);
  };

  // Sauvegarder les modifications
  const handleSaveReview = async () => {
    if (!editingReview) return;
    
    setIsSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/products/admin/reviews/${editingReview.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          author_name: editForm.author_name,
          rating: editForm.rating,
          title: {
            fr: editForm.title_fr,
            en: editForm.title_en,
            he: editForm.title_he
          },
          content: {
            fr: editForm.content_fr,
            en: editForm.content_en,
            he: editForm.content_he
          }
        })
      });
      
      if (res.ok) {
        toast.success('Avis mis à jour avec succès');
        setShowEditModal(false);
        setEditingReview(null);
        fetchReviews(token, activeTab);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erreur lors de la mise à jour');
      }
    } catch (err) {
      toast.error('Erreur de connexion');
    } finally {
      setIsSaving(false);
    }
  };

  // Ouvrir le modal de réécriture IA
  const handleOpenAIRewrite = () => {
    if (selectedReviews.length === 0) {
      toast.error('Sélectionnez au moins un avis');
      return;
    }
    setAIPrompt('Remplace toute référence au nom du produit source par le nom du produit actuel. Garde le même ton et la même longueur.');
    setAIResults([]);
    setAIProgress({ current: 0, total: selectedReviews.length });
    setShowAIModal(true);
  };

  // Lancer la réécriture IA
  const handleAIRewrite = async () => {
    if (!aiPrompt.trim()) {
      toast.error('Veuillez entrer des instructions pour l\'IA');
      return;
    }
    
    setAIProcessing(true);
    setAIProgress({ current: 0, total: selectedReviews.length });
    setAIResults([]);
    
    try {
      const res = await fetch(`${API_URL}/api/products/admin/reviews/ai-rewrite`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          review_ids: selectedReviews,
          instructions: aiPrompt
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setAIResults(data.results || []);
        setAIProgress({ current: data.processed || 0, total: selectedReviews.length });
        
        const successCount = data.results?.filter(r => r.success).length || 0;
        const errorCount = data.results?.filter(r => !r.success).length || 0;
        
        if (successCount > 0) {
          toast.success(`${successCount} avis réécrits avec succès`);
        }
        if (errorCount > 0) {
          toast.error(`${errorCount} avis n'ont pas pu être réécrits`);
        }
        
        // Rafraîchir la liste
        fetchReviews(token, activeTab);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erreur lors de la réécriture IA');
      }
    } catch (err) {
      console.error('AI rewrite error:', err);
      toast.error('Erreur de connexion');
    } finally {
      setAIProcessing(false);
    }
  };

  return (
    <AdminLayout title="Modération des avis" subtitle="Gérer la file d'attente des avis clients">
      <div data-testid="admin-reviews-queue">
        {/* Header actions */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <MessageSquare className="h-6 w-6 text-[#C0000C]" />
              Modération des avis
            </h1>
            <p className="text-gray-400">Gérer la file d'attente des avis clients</p>
          </div>
          
          <Button variant="outline" onClick={() => fetchReviews(token, activeTab)} data-testid="refresh-btn" className="border-zinc-700 text-white">
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualiser
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 rounded-lg bg-yellow-500/10">
                <Clock className="h-6 w-6 text-yellow-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.pending}</p>
                <p className="text-gray-400 text-sm">En attente</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 rounded-lg bg-green-500/10">
                <CheckCircle className="h-6 w-6 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.approved}</p>
                <p className="text-gray-400 text-sm">Approuvés</p>
              </div>
            </CardContent>
          </Card>
        
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-lg bg-red-500/10">
              <XCircle className="h-6 w-6 text-red-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.rejected}</p>
              <p className="text-gray-200 text-sm">Rejetés</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-4">
        <div className="flex items-center justify-between">
          <TabsList className="bg-zinc-900">
            <TabsTrigger value="pending" className="data-[state=active]:bg-yellow-500/20" data-testid="tab-pending">
              En attente ({stats.pending})
            </TabsTrigger>
            <TabsTrigger value="approved" className="data-[state=active]:bg-green-500/20" data-testid="tab-approved">
              Approuvés ({stats.approved})
            </TabsTrigger>
            <TabsTrigger value="rejected" className="data-[state=active]:bg-red-500/20" data-testid="tab-rejected">
              Rejetés ({stats.rejected})
            </TabsTrigger>
          </TabsList>
          
          {selectedReviews.length > 0 && (
            <div className="flex gap-2">
              <Button
                onClick={handleOpenAIRewrite}
                disabled={isProcessing}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="ai-rewrite-btn"
              >
                <Wand2 className="h-4 w-4 mr-2" />
                Réécrire avec IA ({selectedReviews.length})
              </Button>
              {activeTab === 'pending' && (
                <>
                  <Button
                    onClick={() => handleBulkModerate('approve')}
                    disabled={isProcessing}
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="bulk-approve-btn"
                  >
                    {isProcessing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
                    Approuver ({selectedReviews.length})
                  </Button>
                  <Button
                    onClick={() => handleBulkModerate('reject')}
                    disabled={isProcessing}
                    variant="destructive"
                    data-testid="bulk-reject-btn"
                  >
                    {isProcessing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <X className="h-4 w-4 mr-2" />}
                    Rejeter ({selectedReviews.length})
                  </Button>
                </>
              )}
            </div>
          )}
        </div>

        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-mutagim-red" />
          </div>
        ) : reviews.length === 0 ? (
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-12 text-center">
              <MessageSquare className="h-12 w-12 mx-auto text-gray-600 mb-4" />
              <p className="text-gray-200">Aucun avis dans cette catégorie</p>
            </CardContent>
          </Card>
        ) : (
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-0">
              {/* Table Header */}
              <div className="flex items-center gap-4 p-4 border-b border-zinc-800 bg-zinc-800/50">
                <Checkbox
                  checked={selectedReviews.length === reviews.length && reviews.length > 0}
                  onCheckedChange={handleSelectAll}
                  data-testid="select-all"
                />
                <span className="text-sm text-gray-200">Tout sélectionner</span>
              </div>
              
              {/* Reviews List */}
              <div className="divide-y divide-zinc-800">
                {reviews.map((review) => (
                  <div
                    key={review.id}
                    className="p-4 hover:bg-zinc-800/30 transition-colors"
                    data-testid={`review-item-${review.id}`}
                  >
                    <div className="flex items-start gap-4">
                      <Checkbox
                        checked={selectedReviews.includes(review.id)}
                        onCheckedChange={(checked) => handleSelectReview(review.id, checked)}
                        data-testid={`select-review-${review.id}`}
                      />
                      
                      <div className="flex-1 min-w-0">
                        {/* Product Link */}
                        <Link
                          to={`/product/${review.product_slug}`}
                          target="_blank"
                          className="flex items-center gap-2 text-sm text-mutagim-red hover:underline mb-2"
                        >
                          {review.product_name?.[lang] || review.product_name?.fr || 'Produit'}
                          <ExternalLink className="h-3 w-3" />
                        </Link>
                        
                        {/* Author & Rating */}
                        <div className="flex items-center gap-3 mb-2">
                          <span className="font-medium">{review.author_name}</span>
                          <StarRating rating={review.rating} />
                          <span className="text-sm text-gray-500">
                            {new Date(review.created_at).toLocaleDateString('fr-FR')}
                          </span>
                          {review.source === 'imported' && (
                            <Badge variant="outline" className="text-xs">Importé</Badge>
                          )}
                          {review.verified_purchase && (
                            <Badge className="bg-green-500/20 text-green-400 text-xs">Achat vérifié</Badge>
                          )}
                        </div>
                        
                        {/* Title */}
                        {review.title && (
                          <p className="font-medium mb-1">
                            {typeof review.title === 'object' 
                              ? (review.title[lang] || review.title.fr || '') 
                              : review.title}
                          </p>
                        )}
                        
                        {/* Content */}
                        <p className="text-white text-sm">
                          {review.content?.[lang] || review.content?.fr || ''}
                        </p>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex gap-2 items-center">
                        {/* Bouton éditer - toujours visible */}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEditReview(review)}
                          className="border-zinc-700 hover:bg-zinc-800"
                          data-testid={`edit-${review.id}`}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        
                        {activeTab === 'pending' && (
                          <>
                            <Button
                              size="sm"
                              onClick={() => handleModerateOne(review.id, 'approve')}
                              className="bg-green-600 hover:bg-green-700"
                              data-testid={`approve-${review.id}`}
                            >
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleModerateOne(review.id, 'reject')}
                              data-testid={`reject-${review.id}`}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                        
                        {activeTab !== 'pending' && (
                          <Badge className={activeTab === 'approved' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}>
                            {activeTab === 'approved' ? 'Approuvé' : 'Rejeté'}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Pagination */}
              <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-700">
                <div className="flex items-center gap-4">
                  <p className="text-sm text-gray-400">
                    Affichage {(pagination.page - 1) * pagination.limit + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} sur {pagination.total} avis
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-400">Par page:</span>
                    <select
                      value={pagination.limit}
                      onChange={(e) => {
                        const newLimit = parseInt(e.target.value);
                        setPagination(prev => ({ ...prev, limit: newLimit, page: 1 }));
                        fetchReviews(token, activeTab, 1);
                      }}
                      className="bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-sm text-white"
                    >
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                      <option value={200}>200</option>
                      <option value={500}>500</option>
                    </select>
                  </div>
                </div>
                {pagination.total > pagination.limit && (
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={pagination.page <= 1}
                      onClick={() => {
                        const newPage = pagination.page - 1;
                        setPagination(prev => ({ ...prev, page: newPage }));
                        fetchReviews(token, activeTab, newPage);
                      }}
                      className="border-gray-700 text-white hover:bg-gray-800"
                    >
                      Précédent
                    </Button>
                    <span className="px-3 py-1 text-sm text-gray-300">
                      Page {pagination.page} / {Math.ceil(pagination.total / pagination.limit)}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={pagination.page >= Math.ceil(pagination.total / pagination.limit)}
                      onClick={() => {
                        const newPage = pagination.page + 1;
                        setPagination(prev => ({ ...prev, page: newPage }));
                        fetchReviews(token, activeTab, newPage);
                      }}
                      className="border-gray-700 text-white hover:bg-gray-800"
                    >
                      Suivant
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </Tabs>
      </div>
      
      {/* Modal d'édition d'avis */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] bg-zinc-900 border-zinc-800 text-white overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="h-5 w-5 text-[#C0000C]" />
              Éditer l'avis
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Modifiez les informations de l'avis client
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4 overflow-y-auto flex-1 pr-2">
            {/* Auteur et Note sur la même ligne */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="author_name">Nom de l'auteur</Label>
                <Input
                  id="author_name"
                  value={editForm.author_name}
                  onChange={(e) => setEditForm(prev => ({ ...prev, author_name: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Note</Label>
                <div className="flex items-center gap-2 h-10">
                  <StarRating 
                    rating={editForm.rating} 
                    interactive 
                    onRatingChange={(rating) => setEditForm(prev => ({ ...prev, rating }))}
                  />
                  <span className="text-sm text-gray-400">({editForm.rating}/5)</span>
                </div>
              </div>
            </div>
            
            {/* Section Français */}
            <div className="border border-zinc-700 rounded-lg p-4 space-y-3">
              <h4 className="font-medium text-sm text-gray-300 flex items-center gap-2">
                🇫🇷 Français
              </h4>
              <div className="space-y-2">
                <Label htmlFor="title_fr">Titre (optionnel)</Label>
                <Input
                  id="title_fr"
                  value={editForm.title_fr}
                  onChange={(e) => setEditForm(prev => ({ ...prev, title_fr: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700"
                  placeholder="Titre de l'avis"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="content_fr">Contenu</Label>
                <Textarea
                  id="content_fr"
                  value={editForm.content_fr}
                  onChange={(e) => setEditForm(prev => ({ ...prev, content_fr: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700 min-h-[80px]"
                  placeholder="Contenu de l'avis"
                />
              </div>
            </div>
            
            {/* Section Anglais */}
            <div className="border border-zinc-700 rounded-lg p-4 space-y-3">
              <h4 className="font-medium text-sm text-gray-300 flex items-center gap-2">
                🇬🇧 English
              </h4>
              <div className="space-y-2">
                <Label htmlFor="title_en">Title (optional)</Label>
                <Input
                  id="title_en"
                  value={editForm.title_en}
                  onChange={(e) => setEditForm(prev => ({ ...prev, title_en: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700"
                  placeholder="Review title"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="content_en">Content</Label>
                <Textarea
                  id="content_en"
                  value={editForm.content_en}
                  onChange={(e) => setEditForm(prev => ({ ...prev, content_en: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700 min-h-[80px]"
                  placeholder="Review content"
                />
              </div>
            </div>
            
            {/* Section Hébreu */}
            <div className="border border-zinc-700 rounded-lg p-4 space-y-3">
              <h4 className="font-medium text-sm text-gray-300 flex items-center gap-2">
                🇮🇱 עברית
              </h4>
              <div className="space-y-2">
                <Label htmlFor="title_he">כותרת (אופציונלי)</Label>
                <Input
                  id="title_he"
                  value={editForm.title_he}
                  onChange={(e) => setEditForm(prev => ({ ...prev, title_he: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700 text-right"
                  dir="rtl"
                  placeholder="כותרת הביקורת"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="content_he">תוכן</Label>
                <Textarea
                  id="content_he"
                  value={editForm.content_he}
                  onChange={(e) => setEditForm(prev => ({ ...prev, content_he: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700 min-h-[80px] text-right"
                  dir="rtl"
                  placeholder="תוכן הביקורת"
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowEditModal(false)}
              className="border-zinc-700"
            >
              Annuler
            </Button>
            <Button
              onClick={handleSaveReview}
              disabled={isSaving || !editForm.author_name || !editForm.content_fr}
              className="bg-[#C0000C] hover:bg-[#A0000A]"
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Enregistrement...
                </>
              ) : (
                'Enregistrer'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Modal de réécriture IA */}
      <Dialog open={showAIModal} onOpenChange={setShowAIModal}>
        <DialogContent className="max-w-2xl bg-zinc-900 border-zinc-800 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 className="h-5 w-5 text-purple-500" />
              Réécrire les avis avec l'IA
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              {selectedReviews.length} avis sélectionné(s). L'IA va réécrire le contenu selon vos instructions.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="ai_prompt">Instructions pour l'IA</Label>
              <Textarea
                id="ai_prompt"
                value={aiPrompt}
                onChange={(e) => setAIPrompt(e.target.value)}
                className="bg-zinc-800 border-zinc-700 min-h-[120px]"
                placeholder="Ex: Remplace les références au produit source par le nom du produit actuel..."
                disabled={aiProcessing}
              />
              <p className="text-xs text-gray-500">
                L'IA utilisera le nom du produit associé à chaque avis pour remplacer les références incorrectes.
              </p>
            </div>
            
            {/* Suggestions de prompts */}
            <div className="space-y-2">
              <Label className="text-sm text-gray-400">Suggestions :</Label>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setAIPrompt('Remplace toute référence au nom du produit source par le nom du produit actuel. Garde le même ton et la même longueur.')}
                  className="text-xs border-zinc-700 hover:bg-zinc-800"
                  disabled={aiProcessing}
                >
                  Corriger nom produit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setAIPrompt('Réécris cet avis de manière plus naturelle et authentique. Utilise le nom du produit actuel et garde un ton positif.')}
                  className="text-xs border-zinc-700 hover:bg-zinc-800"
                  disabled={aiProcessing}
                >
                  Rendre plus naturel
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setAIPrompt('Traduis cet avis en gardant le même sens. Assure-toi que le nom du produit est correct dans toutes les langues.')}
                  className="text-xs border-zinc-700 hover:bg-zinc-800"
                  disabled={aiProcessing}
                >
                  Améliorer traductions
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setAIPrompt('Réécris cet avis pour mettre en avant les qualités du produit et inciter à l\'achat. Utilise le nom du produit actuel. Mets en avant la qualité, le style et le rapport qualité-prix. Ton enthousiaste mais authentique.')}
                  className="text-xs border-zinc-700 hover:bg-zinc-800 bg-green-500/10 text-green-400 border-green-700"
                  disabled={aiProcessing}
                >
                  Lead Generation
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setAIPrompt('Réécris cet avis pour un affichage en slide horizontal (bandeau défilant). Format court et percutant (max 100 caractères). Mets en avant le produit avec une phrase accrocheuse. Utilise le nom du produit actuel. Ex: "Ce [produit] est incroyable ! Qualité premium."')}
                  className="text-xs border-zinc-700 hover:bg-zinc-800 bg-blue-500/10 text-blue-400 border-blue-700"
                  disabled={aiProcessing}
                >
                  Format Slide Horizontal
                </Button>
              </div>
            </div>
            
            {/* Progress */}
            {aiProcessing && (
              <div className="space-y-2 p-4 bg-zinc-800 rounded-lg">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-400">Traitement en cours...</span>
                  <span className="text-purple-400">{aiProgress.current}/{aiProgress.total}</span>
                </div>
                <div className="w-full bg-zinc-700 rounded-full h-2">
                  <div 
                    className="bg-purple-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(aiProgress.current / aiProgress.total) * 100}%` }}
                  />
                </div>
              </div>
            )}
            
            {/* Résultats */}
            {aiResults.length > 0 && !aiProcessing && (
              <div className="space-y-2 max-h-[200px] overflow-y-auto">
                <Label className="text-sm text-gray-400">Résultats :</Label>
                <div className="space-y-1">
                  {aiResults.map((result, idx) => (
                    <div 
                      key={idx}
                      className={`text-xs p-2 rounded ${result.success ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}
                    >
                      {result.success ? (
                        <span className="flex items-center gap-1">
                          <CheckCircle className="h-3 w-3" />
                          Avis réécrit avec succès
                        </span>
                      ) : (
                        <span className="flex items-center gap-1">
                          <XCircle className="h-3 w-3" />
                          Erreur: {result.error}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowAIModal(false)}
              className="border-zinc-700"
              disabled={aiProcessing}
            >
              {aiResults.length > 0 ? 'Fermer' : 'Annuler'}
            </Button>
            {aiResults.length === 0 && (
              <Button
                onClick={handleAIRewrite}
                disabled={aiProcessing || !aiPrompt.trim()}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {aiProcessing ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Traitement...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Lancer la réécriture
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}

export default AdminReviewsQueuePage;
